// src/App.js
import React, { useEffect } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useNavigate,
	Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ROLES } from "./config/supabase";
import { GradeProvider } from "./contexts/GradeContext";
import { AcademicYearProvider } from "./contexts/AcademicYearContext";

// Import components
import Auth from "./components/auth/Auth";
import ResetPassword from "./components/auth/ResetPassword";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import DashboardLayout from "./components/layout/DashboardLayout";
import TeacherShellLayout from "./components/layout/TeacherShellLayout";
import TeacherOrDashboardLayout from "./components/layout/TeacherOrDashboardLayout";

// Core pages
import DashboardEntry from "./pages/DashboardEntry";
import LandingPage from "./landing/LandingPage";

// Admin pages
import TeacherCourseUpdates from "./pages/admin/TeacherCourseUpdates";
import AcademicYearSettings from "./pages/admin/AcademicYearSettings";
import WebSettings from "./pages/admin/WebSettings";
import WebStats from "./pages/admin/WebStats";
import SchoolLogos from "./pages/admin/SchoolLogos";
import Newsletters from "./pages/admin/Newsletters";
import OpenPositions from "./pages/admin/OpenPositions";
import Testimonials from "./pages/admin/Testimonials";
import SchoolVisitTracker from "./pages/admin/SchoolVisitTracker";

// CMS pages
import MasterData from "./pages/cms/MasterData";
import ManageUsers from "./pages/cms/ManageUsers";
import GameResultsEdits from "./pages/cms/GameResultsEdits";

// Courses
import ManageCategories from "./pages/courses/ManageCategories";
import ManageContents from "./pages/courses/ManageContents";

// School pages
import Schools from "./pages/schools/ManageSchools";
import SchoolOwnerships from "./pages/schools/SchoolOwnerships";
import SchoolVisits from "./pages/schools/SchoolVisits";
import ServiceWindow from "./pages/schools/ServiceWindow";

// Teacher pages
import FunmathGames from "./pages/teacher/FunmathGames";
import GameData from "./pages/teacher/GameData";
import Schedule from "./pages/teacher/CourseSchedule";
import GameResultsUpload from "./pages/teacher/GameResultsUpload";
import TeacherTopics from "./pages/teacher/module/TeacherTopics";
import TeacherGamesByTopic from "./pages/teacher/module/TeacherGamesByTopic";
import TeacherOnboarding from "./pages/teacher/module/TeacherOnboarding";
import TeacherVideos from "./pages/teacher/module/TeacherVideos";

// Expense pages
import ExpenseTracker from "./pages/expenses/ExpenseTracker";
import ExpenseList from "./pages/expenses/ExpenseList";
import MyExpenses from "./pages/expenses/MyExpenses";

// User pages
import Account from "./pages/user/Account";
import Feedback from "./pages/user/Feedback";

// Reports
import FunmiyoAdminReport from "./pages/reports/FunmiyoAdminReport";
import FunmathAdminReport from "./pages/reports/FunmathAdminReport";
import TeacherSchedule from "./pages/reports/TeacherSchedule";
import ReportSettings from "./pages/reports/ReportSettings";
import ReportGeneration from "./pages/reports/ReportGeneration";
import BulkReports from "./pages/reports/BulkReports";
import FmePerformanceMetrics from "./pages/reports/FmePerformanceMetrics";

import Approvals from "./pages/approvals/Approvals";
import Notifications from "./pages/notifications/Notifications";
import Downloads from "./pages/downloads/Downloads";
import Links from "./pages/links/Links";
import MyNotifications from "./pages/notifications/MyNotifications";
import AllNotifications from "./pages/notifications/AllNotifications";

// Component to handle root route and check for password recovery
const RootRedirect = () => {
	const navigate = useNavigate();

	// Check immediately on mount, before any other effects run
	useEffect(() => {
		// Check if URL has hash with password recovery tokens
		const hash = window.location.hash;
		const searchParams = new URLSearchParams(window.location.search);
		const hasRecoveryHash = hash && (
			hash.includes("type=recovery") ||
			hash.includes("access_token") ||
			hash.includes("#access_token") ||
			hash.includes("refresh_token")
		);
		const hasRecoveryQuery = searchParams.get("type") === "recovery" ||
			searchParams.has("access_token") ||
			searchParams.has("recovery_token");

		if (hasRecoveryHash || hasRecoveryQuery) {
			// Redirect to reset-password with the hash/query preserved
			const redirectPath = hasRecoveryHash
				? `/reset-password${hash}`
				: `/reset-password${window.location.search}${hash || ""}`;
			console.log("RootRedirect: Detected recovery tokens, redirecting to:", redirectPath);
			// Use replace: true to prevent back button issues
			navigate(redirectPath, { replace: true });
			return; // Exit early to prevent dashboard redirect
		}

		// Only redirect to dashboard if no recovery tokens found
		// Add a small delay to ensure hash is checked first
		const timer = setTimeout(() => {
			navigate("/dashboard", { replace: true });
		}, 100);

		return () => clearTimeout(timer);
	}, [navigate]);

	return null; // This component only handles redirects
};

function App() {
	return (
		<AuthProvider>
			<AcademicYearProvider>
				<GradeProvider>
					<Router>
					<Routes>
						{/* Public routes */}
						<Route path="/login" element={<Auth />} />
						<Route path="/reset-password" element={<ResetPassword />} />
						{/* Marketing landing page (from funmiyo-web) */}
						<Route path="/" element={<LandingPage />} />
						{/* Authenticated home shortcut */}
						<Route path="/app" element={<RootRedirect />} />

						{/* Protected routes - all authenticated users */}
						<Route
							path="/dashboard"
							element={
								<ProtectedRoute>
									<TeacherOrDashboardLayout>
										<DashboardEntry />
									</TeacherOrDashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Funmiyo Admin Routes */}
						<Route
							path="/reports/funmiyo-admin-report"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<FunmiyoAdminReport />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/reports/funmath-owners-report"
							element={
								<ProtectedRoute
									allowedRoles={[ROLES.FUNMATH_ADMIN, ROLES.FUNMIYO_EMPLOYEE]}>
									<DashboardLayout>
										<FunmathAdminReport />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/reports/teacher-schedule"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<TeacherSchedule />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/reports/fme-performance-metrics"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<FmePerformanceMetrics />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/teacher-course-updates"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<TeacherCourseUpdates />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/academic-year-settings"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<AcademicYearSettings />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/web-settings"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<WebSettings />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/web-stats"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<WebStats />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/school-logos"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<SchoolLogos />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/newsletters"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<Newsletters />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/open-positions"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<OpenPositions />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/testimonials"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<Testimonials />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/school-visit-tracker"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<SchoolVisitTracker />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/master-data"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<MasterData />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/game-results-edits"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.VIDEO_EDITOR,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<GameResultsEdits />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/manage-users"
							element={
								<ProtectedRoute
									allowedRoles={[ROLES.FUNMIYO_ADMIN, ROLES.FUNMIYO_EMPLOYEE]}>
									<DashboardLayout>
										<ManageUsers />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/approvals"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<Approvals />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/notifications"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<Notifications />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/all-notifications"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<AllNotifications />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/my-notifications"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
										ROLES.SCHOOL_ADMIN,
										ROLES.SCHOOL_TEACHER,
										ROLES.VIDEO_EDITOR,
									]}
									requireActiveProfile>
									<TeacherOrDashboardLayout>
										<MyNotifications />
									</TeacherOrDashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* CMS Routes */}
						<Route
							path="/manage-games"
							element={
								<ProtectedRoute
									allowedRoles={[ROLES.FUNMIYO_ADMIN, ROLES.FUNMATH_ADMIN]}>
									<DashboardLayout>
										<ManageCategories />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>
						<Route
							path="/manage-course-contents/:categoryId"
							element={
								<ProtectedRoute
									allowedRoles={[ROLES.FUNMIYO_ADMIN, ROLES.FUNMATH_ADMIN]}>
									<DashboardLayout>
										<ManageContents />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* School Routes */}
						<Route
							path="/manage-schools"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<Schools />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/school-ownerships"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<SchoolOwnerships />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/school-visits"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<SchoolVisits />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/service-window"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<ServiceWindow />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Report Routes */}
						<Route
							path="/report-settings"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<ReportSettings />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/report-generation"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN, ROLES.FUNMATH_ADMIN, ROLES.FUNMIYO_EMPLOYEE, ROLES.SCHOOL_ADMIN]}>
									<DashboardLayout>
										<ReportGeneration />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/bulk-reports"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN, ROLES.FUNMATH_ADMIN, ROLES.FUNMIYO_EMPLOYEE, ROLES.SCHOOL_ADMIN]}>
									<DashboardLayout>
										<BulkReports />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Teacher Routes — full-width shell (no sidebar / no grade header) */}
						<Route
							element={
								<ProtectedRoute
									allowedRoles={[ROLES.SCHOOL_TEACHER]}
									requireActiveProfile>
									<TeacherShellLayout />
								</ProtectedRoute>
							}>
							<Route
								path="/teacher"
								element={<Navigate to="/dashboard" replace />}
							/>
							<Route
								path="/teacher/topics/:gradeId"
								element={<TeacherTopics />}
							/>
							<Route
								path="/teacher/games/:gradeId/:topicId"
								element={<TeacherGamesByTopic />}
							/>
							<Route
								path="/teacher/videos/:gradeId/:categoryId"
								element={<TeacherVideos />}
							/>
							<Route path="/teacher/onboarding" element={<TeacherOnboarding />} />
							<Route path="/schedule" element={<Schedule />} />
							<Route path="/game-results-upload" element={<GameResultsUpload />} />
							<Route path="/game-data/:categoryId" element={<GameData />} />
							<Route path="/funmath-games-legacy" element={<FunmathGames />} />
						</Route>

						<Route
							path="/funmath-games"
							element={<Navigate to="/dashboard" replace />}
						/>
						<Route
							path="/teacher/account"
							element={<Navigate to="/account" replace />}
						/>

						{/* Downloads - Admin/Employee/VideoEditor only (no School Teacher) */}
						<Route
							path="/downloads"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
										ROLES.VIDEO_EDITOR,
									]}>
									<DashboardLayout>
										<Downloads />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Central Tracker - same access as Downloads; add/edit/delete admin only */}
						<Route
							path="/central-tracker"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
										ROLES.VIDEO_EDITOR,
									]}>
									<DashboardLayout>
										<Links />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Expense Routes */}
						<Route
							path="/submit-expense"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<ExpenseTracker />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/my-expenses"
							element={
								<ProtectedRoute
									allowedRoles={[
										ROLES.FUNMIYO_ADMIN,
										ROLES.FUNMATH_ADMIN,
										ROLES.FUNMIYO_EMPLOYEE,
									]}>
									<DashboardLayout>
										<MyExpenses />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/expense-reports"
							element={
								<ProtectedRoute allowedRoles={[ROLES.FUNMIYO_ADMIN]}>
									<DashboardLayout>
										<ExpenseList />
									</DashboardLayout>
								</ProtectedRoute>
							}
						/>

						{/* Common User Routes */}
						<Route
							path="/account"
							element={
								<ProtectedRoute>
									<TeacherOrDashboardLayout>
										<Account />
									</TeacherOrDashboardLayout>
								</ProtectedRoute>
							}
						/>

						<Route
							path="/feedback"
							element={
								<ProtectedRoute>
									<TeacherOrDashboardLayout>
										<Feedback />
									</TeacherOrDashboardLayout>
								</ProtectedRoute>
							}
						/>

					</Routes>
				</Router>
				</GradeProvider>
			</AcademicYearProvider>
		</AuthProvider>
	);
}

export default App;
